import React from 'react';
import _ from 'lodash';
import {
  Stack,
  FormControl,
  Typography,
  Fab,
  TextField,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import { pl } from 'date-fns/locale';
import { CssTextField } from '../others/CssTextField';
import { formatDate, handleShowOn } from '../../services/functions';
import { useResponsive } from '../../services/hooks';
import { RequiredField } from '../others/RequiredField';

const Row = ({ numRow, index, field, formik, originFieldName, originalField }) => {

  const currentRow = formik.values?.[originFieldName]?.[numRow] || {};
  const value = currentRow[field] ?? '';

  const fieldError =
    formik.touched?.[originFieldName]?.[numRow]?.[field] &&
    Boolean(formik.errors?.[originFieldName]?.[numRow]?.[field]);

  const errorMessage =
    formik.errors?.[originFieldName]?.[numRow]?.[field];

  const setValue = val => {

    const updated = [...formik.values[originFieldName]];

    updated[numRow] = {
      ...updated[numRow],
      [field]: val
    };

    formik.setFieldValue(originFieldName, updated);
  };

  const renderTextField = () => (
    <CssTextField
      error={fieldError}
      helperText={fieldError ? errorMessage : ''}
      fullWidth
      value={value}
      onChange={e => setValue(e.target.value)}
      variant="outlined"
    />
  );

  return (
    <FormControl fullWidth error={fieldError}>
      <Typography variant="subtitle1">
        <b>{field}</b>
      </Typography>

      {!originalField.multichar_types
        ? renderTextField()
        : originalField.multichar_types[index] === 'string'
          ? renderTextField()
          : originalField.multichar_types[index] === 'date'
            ? (
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={pl}>
                <DatePicker
                  inputFormat="dd.MM.yyyy"
                  value={value || null}
                  onChange={val => setValue(formatDate(val))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={fieldError}
                      helperText={fieldError ? errorMessage : ''}
                      fullWidth
                    />
                  )}
                />
              </LocalizationProvider>
            )
            : originalField.multichar_types[index] === 'number'
              ? (
                <>
                  <Select
                    fullWidth
                    value={value}
                    onChange={e => setValue(e.target.value)}
                  >
                    {[...Array(100)].map((_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {i + 1}
                      </MenuItem>
                    ))}
                  </Select>

                  {fieldError && (
                    <Typography variant="caption" color="error">
                      {errorMessage}
                    </Typography>
                  )}
                </>
              )
              : originalField.multichar_types[index] === 'choice'
                ? (
                  <>
                    <Select
                      fullWidth
                      value={value}
                      onChange={e => setValue(e.target.value)}
                    >
                      {originalField.dynamicOptions?.map((dos, key) => {

                        const disabled = formik.values[originFieldName]
                          .some(fv => fv['Zajęcie'] == dos.id);

                        return (
                          <MenuItem
                            disabled={disabled}
                            key={key}
                            value={dos.id}
                          >
                            {dos.name}
                          </MenuItem>
                        );
                      })}
                    </Select>

                    {fieldError && (
                      <Typography variant="caption" color="error">
                        {errorMessage}
                      </Typography>
                    )}
                  </>
                )
                : null}

    </FormControl>
  );
};

export const MultiCharField = props => {

  const { field, formik, getFieldCallback } = props;
  const isDesktop = useResponsive('up', 'lg');

  const rows = formik.values[field.name] || [];

  const globalError =
    typeof formik.errors?.[field.name] === 'string';

  const globalErrorMessage = formik.errors?.[field.name];

  const onAdd = () => {

    const updated = [...rows, {}];

    formik.setFieldValue(field.name, updated);
    formik.setFieldTouched(field.name, true, false);
  };

  const onRemove = () => {

    if (rows.length === 0) {
      return;
    }

    const updated = rows.slice(0, -1);

    formik.setFieldValue(field.name, updated);
    formik.setFieldTouched(field.name, true, false);
  };

  const getTitle = () => {

    if (field.multichar_title) {
      return field.multichar_title;
    }

    if (field.name === 'friends') {
      return 'Dodaj znajomego';
    }

    if (field.name === 'siblings') {
      return 'Dodaj brata / siostrę';
    }

    return 'Dodaj dziecko';
  };

  const getRowStyle = () => isDesktop
    ? { marginBottom: '10px', padding: '5px' }
    : {
      border: '1px solid',
      borderColor: 'secondary.main',
      marginTop: '10px',
      marginBottom: '5px',
      padding: '10px',
      borderRadius: '8px',
      boxShadow: '0px 8px 16px rgba(0,0,0,0.1)'
    };

  return (
    <Stack
      direction="column"
      border={1}
      borderColor="rgb(0,171,85)"
      borderRadius={2}
      p={3}
      mt={1}
      mb={2}
      boxShadow={3}
      style={{
        display: field.hide
          ? 'none'
          : handleShowOn(
            field.show_on,
            formik.values,
            getFieldCallback(
              !_.isNil(field.show_on)
                ? field.show_on.trim().split(' ')[0].split('-')[0]
                : []
            )
          )
            ? ''
            : 'none'
      }}
    >

      <Stack justifyContent="center" direction="row" alignItems="center" gap={3} p={2}>

        <Fab color="primary" onClick={onAdd}>
          <AddIcon />
        </Fab>

        <Typography variant="h5" fontWeight={600} color="primary">
          {getTitle()}

          {field.label && (
            <RequiredField
              required={field.required}
              isEmpty={_.isEmpty(rows)}
              formikError={globalError}
            />
          )}
        </Typography>

        <Fab color="secondary" onClick={onRemove}>
          <RemoveIcon />
        </Fab>

      </Stack>

      {rows.map((_, numRow) => (
        <Stack
          key={numRow}
          gap={3}
          direction={isDesktop ? 'row' : 'column'}
          sx={getRowStyle()}
        >
          {field.multichar_fields.map((mField, key) => (
            <Row
              key={key}
              numRow={numRow}
              index={key}
              formik={formik}
              field={mField}
              originFieldName={field.name}
              originalField={field}
            />
          ))}
        </Stack>
      ))}

      {globalError && (
        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
          {globalErrorMessage}
        </Typography>
      )}

    </Stack>
  );
};
